import { backend } from '@/services/backend'
import type {
  DocGroupListItem,
  DocGroupUserData,
  DocsUserDataResponse,
  ListItemType,
  ListItem,
} from './interfaces'

import type { DocsListView } from './docs.list'

/**
 * Podcasts logic used for listing page.
 */
export class PodcastsView implements DocsListView {
  public podcasts: DocGroupListItem[] = []
  private _hasSetUserData: boolean = false

  constructor(podcasts: DocGroupListItem[]) {
    this.podcasts = podcasts as DocGroupListItem[]
  }

  setUserData(docsUserData?: DocsUserDataResponse): void {
    if (!docsUserData) {
      return
    }

    const docsLength = this.podcasts.length
    for (let i = 0; i < docsLength; i++) {
      const doc = this.podcasts[i]
      const userData = docsUserData[doc.id] as DocGroupUserData

      doc.user = userData || {
        is_followed: false,
      }
    }

    // Sort: followed first, then alphabetically
    this.podcasts.sort((a, b) => {
      if (a.user?.is_followed !== b.user?.is_followed) {
        return a.user?.is_followed ? -1 : 1
      }
      return a.title.localeCompare(b.title)
    })

    this._hasSetUserData = true
  }

  get hasSetUserData(): boolean {
    return this._hasSetUserData
  }

  public get filteredDocs(): ListItem[] {
    return this.podcasts
  }

  public get dataLength(): number {
    return this.podcasts.length
  }

  public get docTypeName(): ListItemType {
    return 'podcast'
  }

  setShowLimit(): void {
    // Not implemented for podcasts
  }

  isDocSortVisible(): boolean {
    return false
  }

  podcastSubtitle(podcast: DocGroupListItem): string {
    const count = podcast.doc_count
    return count + (count === 1 ? ' episode' : ' episodes')
  }

  hasFollowBadge(): boolean {
    return true
  }

  /**
   * Link for the podcast details (listing) page.
   */
  public startLinkParams(doc: ListItem): Record<string, unknown> {
    return {
      name: 'podcastPage',
      params: {
        url_slug: doc.url_slug,
      },
    }
  }

  /**
   * Create a new PodcastsView with search results for the given `searchTerm`.
   */
  static async createSearchView(
    searchTerm: string,
    docsUserData?: DocsUserDataResponse,
  ): Promise<PodcastsView> {
    // Note: other simiar methods (for example, ArticlesView) have an extra
    // `if (searchTerm)` check inside the method.
    //
    // In the UI the case where we don't have the searchTerm corresponds to the
    // "View All" link click on the search page for articles that opens the
    // `/app/articles/list/all` page.
    //
    // But for podcasts the "View All" just open the `/app/podcasts page` (and the
    // same for episodes). So we do not have the "/list/all" page for podcasts,
    // and do not need the `if (searchTerm)`
    //
    // I do not remember why exactly we do not have the `/list/all` page, and if
    // this was an intentional decision or just a shortcut for faster
    // implementation.
    const limit = 100
    const response = await backend.getPodcastSearch(searchTerm, limit)
    const view = new PodcastsView(response.slice(0, 4))
    view.setUserData(docsUserData)

    return view
  }
}
